// A lead is "yours" when you're the assigned agent or a co-agent on it.
// createdby deliberately does NOT count — staff entering a lead on another
// agent's behalf shouldn't surface it under their own name.
//
// ownerlessFallback: an OWNERLESS lead (no assignedAgent) counts for its
// creator. Only the agents' own forced-self boards pass this, so their
// unassigned entries don't vanish on them. The privileged agent PICKER never
// passes it — picking a name means strictly assigned/co-agent; ownerless
// leads are found under "All Agents" instead.
export const isOnLead = (lead, agentId, { ownerlessFallback = false } = {}) =>
  !!agentId &&
  (lead?.assignedAgent === agentId ||
    (Array.isArray(lead?.coAgents) &&
      lead.coAgents.some((c) => c?.id === agentId)) ||
    (ownerlessFallback && !lead?.assignedAgent && lead?.createdby === agentId));

// Who should own a lead minted by the board→pipeline sweep: the deal's agent,
// else the deal's creator, else NOBODY ("") — the sweep must then leave the
// deal on the board rather than guess. It must NEVER fall back to the user
// running the sweep: that put another agent's file on the admin's own board
// as a lead "assigned" to them (2026-07-29 live). Legacy deals sometimes hold
// object-shaped agent fields (names, not ids) — only string/number ids count.
export const migrationLeadOwner = (deal = {}) => {
  const id = (v) =>
    typeof v === "string" || typeof v === "number" ? String(v).trim() : "";
  return id(deal.agentData) || id(deal.createdby) || "";
};

// Deals the sweep must SKIP (no resolvable owner) render in NO normal view —
// the board retired the Prospect / Listing Appointment columns — so the
// pipeline shows them in an explicit "needs an agent" banner instead of
// letting them strand invisibly (the 831 Fairview failure mode, deal flavor).
export const sweepStuckDeals = (deals, sweptStages) =>
  (deals || []).filter(
    (d) =>
      (sweptStages || []).includes(d?.dealStage) &&
      !(Number(d?.deletedAt) > 0) &&
      !d?.migratedToLeadId &&
      d?.requestType !== "fileRequest" &&
      migrationLeadOwner(d) === "",
  );

// Leads the OLD sweep may have mis-minted (live 2026-07-29): it fell back to
// the admin running it, and ensureLeadOwner then made assignedAgent ===
// createdby === that admin. Candidates: a MOVED lead (sourceDealId) owned by
// an Admin with that untouched signature. The caller must then fetch each
// candidate's source deal BY ID (it is soft-deleted, so it is absent from the
// loaded deals list) and confirm migrationLeadOwner(sourceDeal) === "" — only
// then was the owner invented. A human re-assign breaks the signature, so
// already-fixed leads drop off on their own.
export const suspectCandidateLeads = (leads, adminIds) => {
  const admins = new Set(adminIds || []);
  return (leads || []).filter((l) => {
    if (!l?.sourceDealId) return false;
    if (!l.assignedAgent || l.assignedAgent !== l.createdby) return false;
    return admins.has(l.assignedAgent);
  });
};

// Never persist a lead without an owner. An unassigned lead is invisible in
// every per-agent pipeline view (it only shows under "All Agents"), so it
// silently "disappears" — 831 Fairview (2026-07-15) was created with no agent
// and couldn't be found. Fall back to whoever created/entered it; a human can
// reassign from the lead drawer. Every saveListingLead / saveBuyerLead runs a
// lead through this, so no creation path can leave one ownerless.
export const ensureLeadOwner = (data = {}) => {
  const assignedAgent =
    data.assignedAgent || data.createdby || data.enteredBy || "";
  if (assignedAgent === data.assignedAgent) return data;
  return { ...data, assignedAgent };
};
